import React, {
  createContext,
  useState,
  useEffect,
  useMemo,
  useContext,
  useCallback,
} from "react";
import { AuthContext } from "./AuthContext";
import api from '../context/api';
import axios from 'axios';

// Create TimerContext
const TimerContext = createContext();

// TimerProvider component
const TimerProvider = ({ children }) => {
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const { token } = useContext(AuthContext);
  const [selectedDay, setSelectedDay] = useState({
    game1: [],
    game2: [],
    sortedGames: [],
  });
  const [commissionPercentage, setCommissionPercentage] = useState(0.1);

  // Helper to interpret a game's closingTime as a time today
  const getClosingTimeAsDate = (closingTime) => {
    if (!closingTime || typeof closingTime !== "string") return null;
    const parts = closingTime.split(":");
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10) || 0;
    const now = new Date();
    now.setHours(hours, minutes, 0, 0);
    return now;
  };

  // Function to fetch game data
  const fetchGameData = useCallback(async () => {
    try {
      // Fetch data from the server (allow public access even when token is missing)
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await api.get(`/games/id`, { headers });
      const data = response.data;
      // Store fetched data in localStorage
      localStorage.setItem("selectedDay", JSON.stringify(data));

      // Update the state with the fetched data
      setSelectedDay(data);
      if (typeof data.commissionPercentage === "number") {
        setCommissionPercentage(data.commissionPercentage);
      }
    } catch (error) {
      console.error("Error fetching game data:", error);
    }
  }, [token]);

  // Load game data from localStorage
  const loadGameDataFromStorage = useCallback(async () => {
    try {
      const storedSelectedDay = localStorage.getItem("selectedDay");

      if (storedSelectedDay) {
        setSelectedDay(JSON.parse(storedSelectedDay));
      }
    } catch (error) {
      console.error("Error loading game data from localStorage:", error);
    }
  }, []);

  useEffect(() => {
    fetchGameData();
  }, [fetchGameData]);

  useEffect(() => {
    loadGameDataFromStorage();
  }, [loadGameDataFromStorage]);

  // Auto-refresh games when any game's time elapses (mirror mobile logic)
  useEffect(() => {
    const checkForElapsedGames = () => {
      const currentTime = new Date();
      const sortedGames = selectedDay.sortedGames || [];

      const hasElapsedGame = sortedGames.some((game) => {
        const closingTimeDate = getClosingTimeAsDate(game.closingTime);
        if (!closingTimeDate) return false;
        return currentTime > closingTimeDate;
      });

      if (hasElapsedGame) {
        fetchGameData();
      }
    };

    const intervalId = setInterval(checkForElapsedGames, 60000); // every 60s
    return () => clearInterval(intervalId);
  }, [selectedDay, fetchGameData]);

  // Function to toggle the dropdown
  const toggleDropdown = useCallback(() => {
    setDropdownVisible(!dropdownVisible);
  }, [dropdownVisible]);

  // Function to select a day
  const selectDay = (selectedDays) => {
    // Preserve the full games lists and sortedGames, only overlay
    // the selected game's fields (d, nextGameDate, closingTime, etc.).
    setSelectedDay((prev) => ({
      ...prev,
      ...selectedDays,
    }));
    toggleDropdown();
  };

  return (
    <TimerContext.Provider
      value={{
        selectDay,
        selectedDay,
        setSelectedDay,
        dropdownVisible,
        setDropdownVisible,
        fetchGameData,
        toggleDropdown,
        commissionPercentage,
      }}
    >
      {children}
    </TimerContext.Provider>
  );
};

export { TimerContext, TimerProvider };
